import React from "react";
import {
  IMAGES,
  LOGO_URL,
  PACKAGES,
  YEARS,
  priceFor,
  BOAT_SPECS,
  BOAT_FEATURES,
  CABINS_DETAIL,
  EXPERIENCES,
  EXPERIENCE_INTRO,
  INCLUDED,
  NOT_INCLUDED,
  EXTRAS,
  PAYMENT_INFO,
  PUBLIC_CALENDARS_BY_YEAR,
  CONTACT,
} from "../data";

// PDF — Light theme (white + ocean blue) using Inter font.
// 7 pages A4 portrait at 96dpi (794 × 1123 px).

const PDF_W = 794;
const PDF_H = 1123;
const PDF_FONT = "'Inter', sans-serif";

const COLOR = {
  bg: "#FFFFFF",
  surface: "#F5F8FC",
  text: "#0F1B33",
  muted: "#5A6B85",
  primary: "#0066B3",
  primaryDark: "#003F73",
  accent: "#00A8E8",
  gold: "#C8A04A",
  border: "#E5E9F0",
  coral: "#E05548",
  green: "#1FA866",
};

const page = {
  width: `${PDF_W}px`,
  minHeight: `${PDF_H}px`,
  padding: "56px 52px",
  background: COLOR.bg,
  color: COLOR.text,
  fontFamily: PDF_FONT,
  position: "relative",
  pageBreakAfter: "always",
  boxSizing: "border-box",
  overflow: "hidden",
};

const overline = {
  fontFamily: PDF_FONT,
  fontSize: "10px",
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  color: COLOR.primary,
  fontWeight: 700,
};

const h1 = {
  fontFamily: PDF_FONT,
  fontSize: "76px",
  lineHeight: "0.95",
  fontWeight: 800,
  letterSpacing: "-0.045em",
  margin: 0,
  color: COLOR.text,
};

const h2 = {
  fontFamily: PDF_FONT,
  fontSize: "36px",
  lineHeight: "1.1",
  fontWeight: 700,
  letterSpacing: "-0.03em",
  margin: 0,
  color: COLOR.text,
};

const body = {
  fontFamily: PDF_FONT,
  fontSize: "13px",
  lineHeight: "1.7",
  color: COLOR.muted,
  fontWeight: 400,
};

const card = {
  background: COLOR.surface,
  border: `1px solid ${COLOR.border}`,
  padding: "20px",
};

const PageHeader = ({ pageNum, total }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 14, borderBottom: `1px solid ${COLOR.border}`, marginBottom: 28 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <img src={LOGO_URL} crossOrigin="anonymous" alt="" style={{ height: 32, width: "auto" }} />
      <div>
        <div style={{ fontFamily: PDF_FONT, fontSize: 11, fontWeight: 700, color: COLOR.text, letterSpacing: "-0.01em" }}>
          Apecatu Expedições
        </div>
        <div style={{ fontFamily: PDF_FONT, fontSize: 8, letterSpacing: "0.18em", textTransform: "uppercase", color: COLOR.muted, fontWeight: 500 }}>
          Living Aboard · Caravelas, BA
        </div>
      </div>
    </div>
    <div style={{ ...overline, fontSize: 9, color: COLOR.muted }}>
      Abrolhos 2026 · 2027 · {pageNum}/{total}
    </div>
  </div>
);

// Preço por ano (2026 e 2027) de um pacote, em linhas compactas.
const PriceRows = ({ pkg, mode }) => (
  <div style={{ marginTop: 4 }}>
    {YEARS.map((y) => (
      <div key={y} style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 2 }}>
        <span style={{ ...overline, fontSize: 8, color: COLOR.muted, letterSpacing: "0.15em", width: 28 }}>{y}</span>
        <span style={{ fontFamily: PDF_FONT, fontSize: 18, fontWeight: 800, color: COLOR.text, letterSpacing: "-0.02em" }}>
          R$ {priceFor(pkg, mode, y).toLocaleString("pt-BR")}
          <span style={{ fontSize: 9, color: COLOR.muted, fontWeight: 400 }}>/pessoa</span>
        </span>
      </div>
    ))}
  </div>
);

const AgendaGrid = ({ months }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 18 }}>
    {months.map((m) => (
      <div key={m.month} style={{ ...card, padding: 14 }}>
        <div style={{ fontFamily: PDF_FONT, fontSize: 14, fontWeight: 800, color: COLOR.text, marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${COLOR.border}`, letterSpacing: "-0.01em" }}>
          {m.month}
        </div>
        {m.dates.map((d) => {
          const avail = d.status === "available";
          return (
            <div
              key={d.range}
              style={{
                fontSize: 10,
                padding: "6px 9px",
                marginBottom: 4,
                background: avail ? "rgba(0,102,179,0.08)" : "rgba(229,233,240,0.6)",
                border: `1px solid ${avail ? COLOR.primary : COLOR.border}`,
                borderLeftWidth: 3,
                color: avail ? COLOR.text : COLOR.muted,
                fontWeight: avail ? 600 : 400,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{d.range}</span>
              {d.special ? (
                <span style={{ fontSize: 7.5, padding: "2px 6px", background: COLOR.coral, color: "#fff", letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase" }}>
                  {d.special}
                </span>
              ) : !avail ? (
                <span style={{ fontSize: 7.5, color: COLOR.muted, letterSpacing: "0.1em", textTransform: "uppercase" }}>res.</span>
              ) : null}
            </div>
          );
        })}
      </div>
    ))}
  </div>
);

const PdfDocument = React.forwardRef((props, ref) => {
  const TOTAL = 7;

  return (
    <div ref={ref} style={{ background: COLOR.bg }}>
      {/* ============= PAGE 1 — COVER ============= */}
      <div style={{ ...page, padding: 0 }}>
        {/* Top white band with logo */}
        <div style={{ padding: "44px 52px 0", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <img src={LOGO_URL} crossOrigin="anonymous" alt="Apecatu Expedições" style={{ height: 80, width: "auto" }} />
          <div style={{ textAlign: "right" }}>
            <div style={{ ...overline, fontSize: 9, color: COLOR.muted }}>Brochura</div>
            <div style={{ fontFamily: PDF_FONT, fontSize: 22, fontWeight: 800, color: COLOR.primary, letterSpacing: "-0.02em" }}>
              2026 · 2027
            </div>
          </div>
        </div>

        {/* Hero image */}
        <div style={{ position: "relative", margin: "32px 52px 0", height: 460, overflow: "hidden" }}>
          <img src={IMAGES.hero} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,63,115,0.0) 30%, rgba(0,63,115,0.55) 100%)" }} />
          <div style={{ position: "absolute", bottom: 20, left: 22 }}>
            <div style={{ fontFamily: PDF_FONT, fontSize: 10, color: "#fff", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600 }}>
              Living Aboard · Caravelas — BA
            </div>
          </div>
        </div>

        {/* Title block */}
        <div style={{ padding: "32px 52px 0" }}>
          <h1 style={h1}>
            Abrolhos<span style={{ color: COLOR.accent }}>.</span>
          </h1>
          <div style={{ display: "flex", gap: 20, marginTop: 20, alignItems: "flex-start" }}>
            <div style={{ width: 4, alignSelf: "stretch", background: COLOR.accent }} />
            <p style={{ ...body, fontSize: 14, color: COLOR.text, fontWeight: 500, maxWidth: 560, margin: 0 }}>
              Quatro dias entre baleias jubarte, naufrágios e chapeirões. A bordo do
              catamarã ZEUS, no único parque marinho do Atlântico Sul que ainda surpreende
              mergulhadores experientes.
            </p>
          </div>

          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginTop: 40, paddingTop: 24, borderTop: `1px solid ${COLOR.border}` }}>
            {[
              ["11", "Hóspedes"],
              ["04", "Cabines"],
              ["11", "Mergulhos"],
              ["50'", "Catamarã"],
            ].map(([n, l]) => (
              <div key={l}>
                <div style={{ fontFamily: PDF_FONT, fontSize: 36, fontWeight: 800, color: COLOR.primary, letterSpacing: "-0.04em", lineHeight: 1 }}>
                  {n}
                </div>
                <div style={{ ...overline, marginTop: 6, color: COLOR.muted }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============= PAGE 2 — EXPERIÊNCIA ============= */}
      <div style={page}>
        <PageHeader pageNum={2} total={TOTAL} />

        <div style={{ ...overline, marginBottom: 12 }}>A experiência</div>
        <h2 style={h2}>
          Um arquipélago que poucos conhecem.
          <span style={{ display: "block", color: COLOR.primary }}>Você dorme dentro dele.</span>
        </h2>
        <p style={{ ...body, marginTop: 16, maxWidth: 620 }}>
          {EXPERIENCE_INTRO}
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 28 }}>
          {EXPERIENCES.slice(0, 6).map((exp, idx) => (
            <div key={exp.title} style={{ position: "relative", height: 200, overflow: "hidden", border: `1px solid ${COLOR.border}` }}>
              <img src={IMAGES[exp.img]} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: exp.objectPosition || "center center" }} />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(15,27,51,0) 30%, rgba(15,27,51,0.88) 95%)" }} />
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: 12 }}>
                <div style={{ fontFamily: PDF_FONT, fontSize: 9, letterSpacing: "0.2em", color: COLOR.accent, fontWeight: 700 }}>0{idx + 1}</div>
                <div style={{ fontFamily: PDF_FONT, fontSize: 15, color: "#fff", marginTop: 4, fontWeight: 700, letterSpacing: "-0.01em" }}>
                  {exp.title}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ ...card, marginTop: 30, borderLeft: `3px solid ${COLOR.accent}` }}>
          <div style={{ ...overline, marginBottom: 10 }}>Um dia típico a bordo</div>
          <p style={{ ...body, fontSize: 12, color: COLOR.text }}>
            <strong>07:00</strong> Saída de Caravelas · Navegação pela manhã ·
            Mergulhos diurnos em naufrágios e chapeirões · Almoço gourmet a bordo ·
            Apneia, SUP ou caiaque no fim da tarde · Pôr do sol na Ilha de Santa
            Bárbara · Jantar sob as estrelas · Mergulho noturno para avançados ·
            Pernoite no arquipélago.
          </p>
        </div>
      </div>

      {/* ============= PAGE 3 — ZEUS ============= */}
      <div style={page}>
        <PageHeader pageNum={3} total={TOTAL} />

        <div style={{ ...overline, marginBottom: 12 }}>Sua casa flutuante</div>
        <h2 style={{ ...h2, fontSize: 60, lineHeight: 1, fontWeight: 800, letterSpacing: "-0.04em" }}>
          ZEUS<span style={{ color: COLOR.accent }}>.</span>
        </h2>
        <div style={{ fontFamily: PDF_FONT, fontSize: 16, color: COLOR.muted, marginTop: 8, fontWeight: 500 }}>
          50 pés. Onze hóspedes. Quatro cabines.
        </div>

        <div style={{ position: "relative", marginTop: 22, height: 260, overflow: "hidden", border: `1px solid ${COLOR.border}` }}>
          <img src={IMAGES.catamaran} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          <div style={{ position: "absolute", bottom: 10, left: 12, background: "rgba(255,255,255,0.95)", color: COLOR.text, padding: "4px 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            Zeus Explorer
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8, height: 115 }}>
          <div style={{ position: "relative", overflow: "hidden", border: `1px solid ${COLOR.border}` }}>
            <img src={IMAGES.zeusAerial} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{ position: "absolute", bottom: 6, left: 8, background: "rgba(255,255,255,0.95)", color: COLOR.text, padding: "3px 7px", fontSize: 8, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>
              Vista aérea
            </div>
          </div>
          <div style={{ position: "relative", overflow: "hidden", border: `1px solid ${COLOR.border}` }}>
            <img src={IMAGES.zeusSala} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{ position: "absolute", bottom: 6, left: 8, background: "rgba(255,255,255,0.95)", color: COLOR.text, padding: "3px 7px", fontSize: 8, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>
              Sala de estar
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8, height: 115 }}>
          <div style={{ position: "relative", overflow: "hidden", border: `1px solid ${COLOR.border}` }}>
            <img src={IMAGES.zeusCabinCasal} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{ position: "absolute", bottom: 6, left: 8, background: "rgba(255,255,255,0.95)", color: COLOR.text, padding: "3px 7px", fontSize: 8, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>
              Quarto casal
            </div>
          </div>
          <div style={{ position: "relative", overflow: "hidden", border: `1px solid ${COLOR.border}` }}>
            <img src={IMAGES.zeusCabinTriplo} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            <div style={{ position: "absolute", bottom: 6, left: 8, background: "rgba(255,255,255,0.95)", color: COLOR.text, padding: "3px 7px", fontSize: 8, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase" }}>
              Quarto triplo
            </div>
          </div>
        </div>

        {/* Specs */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px 22px", marginTop: 18 }}>
          {BOAT_SPECS.map((s) => (
            <div key={s.label} style={{ paddingLeft: 26, position: "relative", paddingBottom: 6, borderBottom: `1px solid ${COLOR.border}` }}>
              <div style={{ position: "absolute", left: 0, top: 7, width: 18, height: 2, background: COLOR.accent }} />
              <div style={{ ...overline, fontSize: 7.5 }}>{s.label}</div>
              <div style={{ fontFamily: PDF_FONT, fontSize: 12, marginTop: 2, fontWeight: 700, color: COLOR.text }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Cabin breakdown + Features */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 14 }}>
          <div style={{ ...card, padding: 14 }}>
            <div style={{ ...overline, marginBottom: 6, fontSize: 9 }}>Distribuição das cabines</div>
            {CABINS_DETAIL.map((c) => (
              <div key={c} style={{ fontSize: 10, color: COLOR.text, marginBottom: 4, display: "flex", gap: 6 }}>
                <span style={{ color: COLOR.accent, fontWeight: 800 }}>•</span>
                <span>{c}</span>
              </div>
            ))}
          </div>
          <div style={{ ...card, padding: 14 }}>
            <div style={{ ...overline, marginBottom: 6, fontSize: 9 }}>A bordo, tudo isso</div>
            {BOAT_FEATURES.slice(0, 5).map((f) => (
              <div key={f} style={{ fontSize: 9.5, color: COLOR.text, marginBottom: 3, display: "flex", gap: 6 }}>
                <span style={{ color: COLOR.green, fontWeight: 800 }}>✓</span>
                <span>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============= PAGE 4 — PACOTES ============= */}
      <div style={page}>
        <PageHeader pageNum={4} total={TOTAL} />

        <div style={{ ...overline, marginBottom: 12 }}>Pacotes</div>
        <h2 style={h2}>Escolha o ritmo da sua imersão.</h2>
        <p style={{ ...body, marginTop: 12, maxWidth: 620 }}>
          Saídas de 2, 3 e 4 dias · Com ou sem mergulho · Crianças até 6 anos não pagam,
          até 12 anos pagam 50% · Pagamento em <strong style={{ color: COLOR.primary }}>3x sem juros</strong>.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 22 }}>
          {PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              style={{
                ...card,
                padding: 16,
                borderColor: pkg.highlight ? COLOR.primary : COLOR.border,
                borderWidth: pkg.highlight ? 2 : 1,
                background: pkg.highlight ? "#F0F7FE" : COLOR.surface,
                position: "relative",
              }}
            >
              {pkg.highlight && (
                <div
                  style={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: COLOR.gold,
                    color: "#fff",
                    fontSize: 8,
                    padding: "4px 10px",
                    letterSpacing: "0.22em",
                    fontWeight: 800,
                    textTransform: "uppercase",
                  }}
                >
                  ★ {pkg.badge}
                </div>
              )}
              <div style={{ fontFamily: PDF_FONT, fontSize: 24, fontWeight: 800, lineHeight: 1, color: COLOR.text, letterSpacing: "-0.03em", marginTop: pkg.highlight ? 6 : 0 }}>
                {pkg.duration}
              </div>
              <div style={{ fontSize: 11, color: COLOR.muted, marginTop: 2 }}>{pkg.nights}</div>

              <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${COLOR.border}` }}>
                <div style={{ ...overline, fontSize: 7.5, color: COLOR.primary }}>Com Mergulho</div>
                <PriceRows pkg={pkg} mode="dive" />
                <div style={{ fontSize: 9.5, color: COLOR.muted, marginTop: 3, lineHeight: 1.4 }}>
                  {pkg.withDive.diveDetail}
                </div>
              </div>

              <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${COLOR.border}` }}>
                <div style={{ ...overline, fontSize: 7.5, color: COLOR.gold }}>Sem Mergulho</div>
                <PriceRows pkg={pkg} mode="no-dive" />
                <div style={{ fontSize: 9.5, color: COLOR.muted, marginTop: 3 }}>
                  Apneia · caiaque · SUP · ilhas
                </div>
              </div>

              <div style={{ fontSize: 8.5, color: COLOR.muted, marginTop: 12, paddingTop: 8, borderTop: `1px dotted ${COLOR.border}` }}>
                + ICMBio R$ {pkg.withDive.icmbio},00 (estr. R$ {pkg.withDive.icmbioForeign},00)
              </div>
            </div>
          ))}
        </div>

        {/* Extras */}
        <div style={{ ...card, marginTop: 18 }}>
          <div style={{ ...overline, marginBottom: 8 }}>Adicionais</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px" }}>
            {EXTRAS.map((e) => (
              <div key={e.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, paddingBottom: 5, borderBottom: `1px dotted ${COLOR.border}` }}>
                <span style={{ color: COLOR.muted }}>{e.label}</span>
                <span style={{ color: COLOR.primary, fontFamily: PDF_FONT, fontSize: 12, fontWeight: 700 }}>
                  {e.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        <p style={{ fontSize: 9, color: COLOR.muted, marginTop: 12, fontStyle: "italic" }}>
          * Mergulhos noturnos apenas para mergulhadores com credenciamento avançado. OPW pode optar por
          batismo noturno ou realizar curso avançado/noturno a bordo (valores sob consulta).
        </p>
      </div>

      {/* ============= PAGE 5 — AGENDA ============= */}
      <div style={page}>
        <PageHeader pageNum={5} total={TOTAL} />

        <div style={{ ...overline, marginBottom: 12 }}>Agenda 2026</div>
        <h2 style={h2}>
          Datas com saída garantida.
          <span style={{ display: "block", color: COLOR.primary }}>Vagas limitadas a 11 por embarque.</span>
        </h2>
        <p style={{ ...body, marginTop: 12, maxWidth: 620 }}>
          Veja as datas confirmadas para a temporada 2026. Saídas com indicação{" "}
          <strong style={{ color: COLOR.primary }}>DISPONÍVEL</strong> ainda têm vagas. Não encontrou o que procura?
          Solicite uma data personalizada com a equipe da Apecatu.
        </p>

        {/* Legend */}
        <div style={{ display: "flex", gap: 18, marginTop: 16, fontSize: 9, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, background: COLOR.primary, display: "inline-block" }} />
            <span style={{ color: COLOR.text, fontWeight: 600 }}>Disponível</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, background: COLOR.border, display: "inline-block" }} />
            <span style={{ color: COLOR.muted }}>Reservado</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, background: COLOR.coral, display: "inline-block" }} />
            <span style={{ color: COLOR.coral, fontWeight: 600 }}>Edição especial</span>
          </div>
        </div>

        <AgendaGrid months={PUBLIC_CALENDARS_BY_YEAR[2026]} />

        <div style={{ ...card, marginTop: 16, background: "#F0F7FE", borderColor: COLOR.primary, borderLeftWidth: 4 }}>
          <div style={{ ...overline, marginBottom: 6 }}>Como reservar</div>
          <div style={{ fontSize: 11, color: COLOR.text, lineHeight: 1.7 }}>
            <strong>1.</strong> Escolha uma data com indicação <strong style={{ color: COLOR.primary }}>DISPONÍVEL</strong> e nos avise pelo WhatsApp.{" "}
            <strong>2.</strong> Pagamento da entrada (20%) confirma sua vaga.{" "}
            <strong>3.</strong> Não encontrou data? Solicite saídas personalizadas para grupos fechados.
          </div>
        </div>
      </div>

      {/* ============= PAGE 6 — AGENDA 2027 ============= */}
      <div style={page}>
        <PageHeader pageNum={6} total={TOTAL} />

        <div style={{ ...overline, marginBottom: 12 }}>Agenda 2027</div>
        <h2 style={h2}>
          Datas de janeiro a março.
          <span style={{ display: "block", color: COLOR.primary }}>Vagas limitadas a 11 por embarque.</span>
        </h2>
        <p style={{ ...body, marginTop: 12, maxWidth: 620 }}>
          Confira as saídas de 2027. Datas com indicação{" "}
          <strong style={{ color: COLOR.primary }}>DISPONÍVEL</strong> ainda têm vagas. Veja quais cabines estão livres
          em apecatu.shop ou fale com a equipe pelo WhatsApp.
        </p>

        {/* Legend */}
        <div style={{ display: "flex", gap: 18, marginTop: 16, fontSize: 9, alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, background: COLOR.primary, display: "inline-block" }} />
            <span style={{ color: COLOR.text, fontWeight: 600 }}>Disponível</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 10, background: COLOR.border, display: "inline-block" }} />
            <span style={{ color: COLOR.muted }}>Reservado</span>
          </div>
        </div>

        <AgendaGrid months={PUBLIC_CALENDARS_BY_YEAR[2027]} />

        <div style={{ ...card, marginTop: 16, background: "#F0F7FE", borderColor: COLOR.primary, borderLeftWidth: 4 }}>
          <div style={{ ...overline, marginBottom: 6 }}>Garanta sua data</div>
          <div style={{ fontSize: 11, color: COLOR.text, lineHeight: 1.7 }}>
            Chame a equipe pelo WhatsApp <strong>{CONTACT.phone}</strong>. A entrada de 20% confirma sua vaga.{" "}
            Precisa de outra data ou de um grupo fechado? Solicite uma saída personalizada.
          </div>
        </div>

        <div style={{ marginTop: 16, height: 330, overflow: "hidden" }}>
          <img src={IMAGES.catamaran} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      </div>

      {/* ============= PAGE 7 — INCLUSO / PAGAMENTO / CONTATO ============= */}
      <div style={page}>
        <PageHeader pageNum={7} total={TOTAL} />

        <div style={{ ...overline, marginBottom: 12 }}>Transparência total</div>
        <h2 style={h2}>O que está, o que não está, e como pagar.</h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 22 }}>
          <div style={card}>
            <div style={{ ...overline, color: COLOR.green, marginBottom: 10 }}>Incluso no pacote</div>
            {INCLUDED.map((i) => (
              <div key={i} style={{ fontSize: 11, marginBottom: 5, display: "flex", gap: 6, color: COLOR.text }}>
                <span style={{ color: COLOR.green, fontWeight: 800 }}>✓</span>
                <span>{i}</span>
              </div>
            ))}
          </div>
          <div style={card}>
            <div style={{ ...overline, color: COLOR.coral, marginBottom: 10 }}>Não incluso</div>
            {NOT_INCLUDED.map((i) => (
              <div key={i} style={{ fontSize: 11, marginBottom: 5, display: "flex", gap: 6, color: COLOR.muted }}>
                <span style={{ color: COLOR.coral, fontWeight: 800 }}>✗</span>
                <span>{i}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment highlight */}
        <div style={{ marginTop: 16, background: COLOR.primary, color: "#fff", padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ ...overline, color: "rgba(255,255,255,0.7)" }}>Forma mais escolhida</div>
            <div style={{ fontFamily: PDF_FONT, fontSize: 24, fontWeight: 800, letterSpacing: "-0.02em", marginTop: 2 }}>
              {PAYMENT_INFO.highlight}
            </div>
          </div>
          <div style={{ fontSize: 10, opacity: 0.85, textAlign: "right", maxWidth: 200 }}>
            Use seu cartão e parcele sem custo adicional. Confirmação rápida.
          </div>
        </div>

        <div style={{ ...card, marginTop: 10 }}>
          <div style={{ ...overline, marginBottom: 8 }}>Outras formas</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
            {PAYMENT_INFO.items.slice(1).map((p) => (
              <div key={p} style={{ fontSize: 10.5, display: "flex", gap: 6, color: COLOR.text }}>
                <span style={{ color: COLOR.primary, fontWeight: 800 }}>•</span>
                <span>{p}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Cancellation */}
        <div style={{ marginTop: 22 }}>
          <div style={{ ...overline, marginBottom: 6 }}>Cancelamento</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              ["30+ dias antes", "Devolução integral de qualquer valor pago."],
              ["Abaixo de 30 dias", "Entrada vira CRÉDITO válido por 12 meses."],
              ["No-show", "Valor pago retido como multa por não comparecimento."],
              ["Imprevisto climático", "Remarcação, devolução ou crédito por 12 meses."],
            ].map(([t, b]) => (
              <div key={t} style={{ ...card, padding: 12 }}>
                <div style={{ fontFamily: PDF_FONT, fontSize: 12, fontWeight: 700, color: COLOR.primary, marginBottom: 4, letterSpacing: "-0.01em" }}>
                  {t}
                </div>
                <div style={{ fontSize: 9.5, color: COLOR.muted, lineHeight: 1.5 }}>{b}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact footer */}
        <div style={{ marginTop: 26, paddingTop: 20, borderTop: `2px solid ${COLOR.primary}`, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div>
            <div style={{ ...overline, marginBottom: 6 }}>Embarque a aventura</div>
            <div style={{ fontFamily: PDF_FONT, fontSize: 22, fontWeight: 800, color: COLOR.text, letterSpacing: "-0.02em" }}>
              {CONTACT.name}
            </div>
            <div style={{ fontSize: 12, color: COLOR.text, marginTop: 6, fontWeight: 600 }}>
              WhatsApp · {CONTACT.phone}
            </div>
            <div style={{ fontSize: 11, color: COLOR.primary, marginTop: 3 }}>{CONTACT.email}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <img src={LOGO_URL} crossOrigin="anonymous" alt="" style={{ height: 50, width: "auto", opacity: 0.9 }} />
            <div style={{ fontSize: 9, color: COLOR.muted, marginTop: 4, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>
              Abrolhos 2026 · 2027
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PdfDocument.displayName = "PdfDocument";
export default PdfDocument;